import { createFileRoute, Link } from '@tanstack/react-router';
import { useServerFn } from '@tanstack/react-start';
import { useState } from 'react';

import { AuthLayout } from '~/components/auth/auth-layout.tsx';
import { ButtonAuth } from '~/components/buttons/button-auth.tsx';
import Field from '~/components/form/Field.tsx';
import { useTranslations } from '~/i18n/use-translations.ts';
import ForgottenPasswordSchema from '~/modules/auth/forgotten-password-schema.ts';
import { getAuthPageDataQueryOptions } from '~/server/auth/auth-page-data-query.ts';
import { sendResetPasswordLinkMutation } from '~/server/auth/send-reset-password-link.ts';
import { cn } from '~/utils/cn.ts';

function validateAuthSearch(search: Record<string, unknown>) {
	return {
		redirectTo: typeof search.redirectTo === 'string' ? search.redirectTo : undefined,
	};
}

export const Route = createFileRoute('/auth/forgotten-password')({
	component: ForgottenPasswordPage,
	loader: ({ context }) => context.queryClient.ensureQueryData(getAuthPageDataQueryOptions()),
	validateSearch: validateAuthSearch,
});

function ForgottenPasswordPage() {
	const t = useTranslations();
	const { redirectTo } = Route.useSearch();
	const sendResetLink = useServerFn(sendResetPasswordLinkMutation);
	const [email, setEmail] = useState('');
	const [emailErrors, setEmailErrors] = useState<string[] | undefined>(undefined);
	const [formError, setFormError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);

	async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		const parsed = ForgottenPasswordSchema.safeParse({
			email,
			redirectTo,
		});

		if (!parsed.success) {
			const fieldErrors = parsed.error.flatten().fieldErrors;
			setEmailErrors(fieldErrors.email);
			setFormError(fieldErrors.custom?.[0] ?? null);
			setIsSuccess(false);
			return;
		}

		setEmailErrors(undefined);
		setFormError(null);
		setIsSubmitting(true);
		setIsSuccess(false);

		try {
			const result = await sendResetLink({ data: parsed.data });
			if (result.status === 'ok') {
				setIsSuccess(true);
				return;
			}

			setFormError(result.formError ?? null);
		} finally {
			setIsSubmitting(false);
		}
	}

	return (
		<AuthLayout>
			<form className="my-2 space-y-6 p-2" onSubmit={handleSubmit}>
				<div>
					<Field
						classNames={{
							container: 'flex flex-col',
							error: { container: '' },
							input: cn('h-9 w-full rounded-none', 'hover:border-foreground-primary', 'bg-muted'),
							label: 'mb-1 text-muted-foreground block text-sm font-medium',
						}}
						errors={emailErrors}
						inputProps={{
							autoComplete: 'email',
							onChange: (event) => setEmail(event.currentTarget.value),
							value: email,
						}}
						labelProps={{ children: t('auth.email') }}
					/>
				</div>
				<ButtonAuth
					dataTestId="send-password-reset-link"
					disabled={isSubmitting}
					text={!isSubmitting ? t('auth.resetPassword') : t('auth.resettingPassword')}
					type="submit"
				/>
				{formError ? <div className="text-negative text-sm italic">{formError}</div> : null}
				{isSuccess ? (
					<div className="flex items-center">
						<div className="text-foreground text-sm italic">{t('auth.resetSent')}</div>
					</div>
				) : null}
				<div className="flex items-center justify-center">
					<div className="text-center text-foreground text-sm">
						{t('auth.goBackTo')}{' '}
						<Link className="text-primary text-sm hover:underline" search={{ redirectTo }} to="/auth/signin">
							{t('auth.signIn')}
						</Link>
					</div>
				</div>
			</form>
		</AuthLayout>
	);
}
